import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, Tabs, Tab, InputAdornment, Dialog,
    DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon, Add as AddIcon,
    CurrencyRupee as RupeeIcon, AccountBalance as AccountIcon,
    Receipt as ReceiptIcon, Print as PrintIcon,
    TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { toast } from 'react-toastify';

const Accounts = () => {
    const [tab, setTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({ type: 'Receipt', category: '', amount: '', narration: '', voucherNo: '' });

    const transactions = [
        { id: 1, voucherNo: 'RCV/2026/001', date: '2026-02-15', type: 'Receipt', category: 'Fee Collection', narration: 'Feb fees - Class 10', amount: 45000, balance: 485000, mode: 'Bank' },
        { id: 2, voucherNo: 'PAY/2026/001', date: '2026-02-14', type: 'Payment', category: 'Salary', narration: 'Staff salary - January', amount: 120000, balance: 440000, mode: 'Bank' },
        { id: 3, voucherNo: 'RCV/2026/002', date: '2026-02-13', type: 'Receipt', category: 'Fee Collection', narration: 'Feb fees - Class 8', amount: 38000, balance: 560000, mode: 'Cash' },
        { id: 4, voucherNo: 'PAY/2026/002', date: '2026-02-12', type: 'Payment', category: 'Stationery', narration: 'Notebooks purchase', amount: 15000, balance: 522000, mode: 'Bank' },
        { id: 5, voucherNo: 'RCV/2026/003', date: '2026-02-10', type: 'Receipt', category: 'Donation', narration: 'Alumni donation', amount: 50000, balance: 537000, mode: 'Cheque' },
        { id: 6, voucherNo: 'PAY/2026/003', date: '2026-02-09', type: 'Payment', category: 'Electricity', narration: 'Feb electricity bill', amount: 8000, balance: 487000, mode: 'Cash' },
        { id: 7, voucherNo: 'PAY/2026/004', date: '2026-02-08', type: 'Payment', category: 'Maintenance', narration: 'Building repair', amount: 25000, balance: 495000, mode: 'Bank' },
        { id: 8, voucherNo: 'RCV/2026/004', date: '2026-02-05', type: 'Receipt', category: 'Fee Collection', narration: 'Jan fees - Class 5', amount: 32000, balance: 520000, mode: 'Cash' },
    ];

    const filteredData = transactions.filter(t => {
        const matchTab = tab === 0 ? true : tab === 1 ? t.type === 'Receipt' : t.type === 'Payment';
        const matchSearch = !searchTerm || t.narration.toLowerCase().includes(searchTerm.toLowerCase()) || t.voucherNo.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchSearch;
    });

    const totalReceipts = transactions.filter(t => t.type === 'Receipt').reduce((a, t) => a + t.amount, 0);
    const totalPayments = transactions.filter(t => t.type === 'Payment').reduce((a, t) => a + t.amount, 0);
    const cashBalance = totalReceipts - totalPayments;

    const chartData = {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
            { label: 'Receipts', data: [82000, 45000, 50000, 38000], backgroundColor: '#4caf50', borderRadius: 4 },
            { label: 'Payments', data: [33000, 120000, 15000, 8000], backgroundColor: '#e53935', borderRadius: 4 },
        ],
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700}>Accounts</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => setOpenDialog(true)}>New Voucher</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />} size="small">Print Ledger</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 22 }} />
                            <Typography variant="body2" color="text.secondary">Total Receipts</Typography>
                            <Typography variant="h5" fontWeight={700} color="success.main">₹{(totalReceipts / 1000).toFixed(0)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #e53935' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <TrendingDownIcon sx={{ color: '#e53935', fontSize: 22 }} />
                            <Typography variant="body2" color="text.secondary">Total Payments</Typography>
                            <Typography variant="h5" fontWeight={700} color="error.main">₹{(totalPayments / 1000).toFixed(0)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #1a9e8f' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <AccountIcon sx={{ color: '#1a9e8f', fontSize: 22 }} />
                            <Typography variant="body2" color="text.secondary">Cash Balance</Typography>
                            <Typography variant="h5" fontWeight={700}>₹{(cashBalance / 1000).toFixed(0)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <ReceiptIcon sx={{ color: '#2196f3', fontSize: 22 }} />
                            <Typography variant="body2" color="text.secondary">Vouchers Today</Typography>
                            <Typography variant="h5" fontWeight={700}>{transactions.filter(t => t.date === '2026-02-15').length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Weekly Cash Flow</Typography>
                            <Box height={200}>
                                <Bar data={chartData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { position: 'top' } },
                                    scales: { y: { beginAtZero: true, grid: { color: '#f0f0f0' } }, x: { grid: { display: false } } }
                                }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${transactions.length})`} />
                    <Tab label={`Receipts (${transactions.filter(t => t.type === 'Receipt').length})`} />
                    <Tab label={`Payments (${transactions.filter(t => t.type === 'Payment').length})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" placeholder="Search by narration or voucher no..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField type="date" fullWidth size="small" label="From" InputLabelProps={{ shrink: true }} value={dateFilter.from} onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField type="date" fullWidth size="small" label="To" InputLabelProps={{ shrink: true }} value={dateFilter.to} onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })} />
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Voucher No.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Narration</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map(t => (
                                    <TableRow key={t.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{t.voucherNo}</TableCell>
                                        <TableCell>{new Date(t.date).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip size="small" icon={t.type === 'Receipt' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                                label={t.type} color={t.type === 'Receipt' ? 'success' : 'error'} variant="outlined" />
                                        </TableCell>
                                        <TableCell>{t.category}</TableCell>
                                        <TableCell>{t.narration}</TableCell>
                                        <TableCell><Chip label={t.mode} size="small" variant="outlined" /></TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: t.type === 'Receipt' ? '#4caf50' : '#e53935' }}>
                                            {t.type === 'Receipt' ? '+' : '-'}₹{t.amount.toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>₹{t.balance.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>

            {/* New Voucher Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Voucher</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField select label="Type" fullWidth value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                            <MenuItem value="Receipt">Receipt</MenuItem>
                            <MenuItem value="Payment">Payment</MenuItem>
                        </TextField>
                        <TextField select label="Category" fullWidth value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                            {formData.type === 'Receipt' ?
                                ['Fee Collection', 'Donation', 'Grant', 'Misc Income'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>) :
                                ['Salary', 'Stationery', 'Electricity', 'Maintenance', 'Transport', 'Misc Expense'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)
                            }
                        </TextField>
                        <TextField label="Narration" fullWidth value={formData.narration} onChange={(e) => setFormData({ ...formData, narration: e.target.value })} />
                        <TextField label="Amount (₹)" fullWidth type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => { toast.success('Voucher created successfully'); setOpenDialog(false); }}>Create Voucher</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Accounts;
